// Gemeinsame Pfad-/Größen-Helfer für Datei-Listen (Dokumente-Explorer, Alte
// Kundenakten). Vorher lagen `humanSize` & Co. doppelt in DocumentsSection.tsx
// und alteKundenakten/types.ts — mit UNTERSCHIEDLICHEN Signaturen, weil
// `KundenaktenFile.sizeBytes` als bigint→string aus dem Backend kommt und
// `Document.fileSize` als number. Hier vereinigt, damit beide Aufrufer dieselbe
// Formatierung teilen.

/** Bytes menschenlesbar; leer/0/ungültig ⇒ Gedankenstrich. */
export const humanSize = (n: number | string | null | undefined): string => {
  let v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};

/**
 * Zerlegt einen gespeicherten Pfad in Verzeichnis und Basename. Legacy-
 * Kundenakten speichern den ordner-relativen Pfad im `fileName`
 * (z. B. "Verträge/2019/Police.pdf").
 */
export const splitPath = (name: string): { dir: string; base: string } => {
  const i = name.lastIndexOf("/");
  return i >= 0 ? { dir: name.slice(0, i), base: name.slice(i + 1) } : { dir: "", base: name };
};

/** Extension OHNE Punkt, klein geschrieben; ein führender Punkt zählt nicht. */
export const extOf = (name: string): string => {
  const i = name.lastIndexOf(".");
  return i >= 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : "";
};

/**
 * Zerlegt einen `fileName` in Verzeichnis, Basename OHNE Extension und
 * Extension (inkl. Punkt) — für den Umbenennen-Dialog: nur der Basename ist
 * editierbar, die Extension bleibt fixer Suffix. Ein führender Punkt zählt (wie
 * im Backend) NICHT als Extension.
 */
export const baseNameParts = (fileName: string): { dir: string; name: string; ext: string } => {
  const { dir, base } = splitPath(fileName);
  const i = base.lastIndexOf(".");
  return i > 0 && i < base.length - 1
    ? { dir, name: base.slice(0, i), ext: base.slice(i) }
    : { dir, name: base, ext: "" };
};

/**
 * Schrägstriche im neuen Dateinamen (T-456): „/" trennt in KMVS Ordner, „\" ist
 * unter Windows verboten. Der Server ersetzt beide durch „-" — vorher schnitt er
 * alles davor ab („Hausrat 2025/2026" wurde still zu „2026.pdf"). Dieselbe
 * Ersetzung hier, damit das Umbenennen-Popup das Ergebnis vorab zeigt.
 */
export const dateinameOhneSchraegstrich = (name: string): string => name.replace(/[\\/]/g, "-");

/** Enthält der eingegebene Name einen Schrägstrich, der ersetzt wird? */
export const hatSchraegstrich = (name: string): boolean => /[\\/]/.test(name);

/**
 * Normalisiert einen Pfad aus einer fremden Quelle für die Baum-Zerlegung:
 * Windows-Backslashes zu "/", mehrfache und führende Slashes weg. Der
 * Host-Sync schreibt `relPath` mit `path.sep` — auf dem Linux-Deploy-Host ist
 * das "/", aber ein einzelner Windows-Scan würde sonst den Baum zerlegen.
 */
export const normalizeRelPath = (p: string): string =>
  p.replace(/\\/g, "/").replace(/\/{2,}/g, "/").replace(/^\//, "");

/** Pfadsegmente ("Verträge/2019/" ⇒ ["Verträge","2019"]). */
export const pathSegments = (path: string): string[] =>
  normalizeRelPath(path).split("/").filter(Boolean);
